import React, {createContext, useContext, useState} from 'react';

import type {SlimTaskInstanceSerializedType} from 'sweet-types';

interface BulkSelectTasksContextType {
  selectedTasks: Array<SlimTaskInstanceSerializedType>;
  setSelectedTasks: (
    selectedTasks: Array<SlimTaskInstanceSerializedType>
  ) => void;
  isIndeterminateState: boolean;
  tasksCount: number;
  setTasksCount: (tasksCount: number) => void;
  handleSelectedTasks: (
    isChecked: boolean,
    allTasks: Array<SlimTaskInstanceSerializedType>
  ) => void;
  addToSelection: (task: SlimTaskInstanceSerializedType) => void;
  removeFromSelection: (task: SlimTaskInstanceSerializedType) => void;
  isTaskSelected: (task: SlimTaskInstanceSerializedType) => boolean;
  enableBulkSelect: boolean;
  setEnableBulkSelect: (enabled: boolean) => void;
}

const BulkSelectContext = createContext<BulkSelectTasksContextType | null>(
  null
);

interface BulkSelectTasksProviderProps {
  children: React.ReactNode;
  enableBulkSelect: boolean;
}

export function BulkSelectTasksProvider({
  children,
  enableBulkSelect: initialEnableBulkSelect,
}: BulkSelectTasksProviderProps) {
  const [selectedTasks, setSelectedTasks] = useState<
    Array<SlimTaskInstanceSerializedType>
  >([]);
  const [enableBulkSelect, setEnableBulkSelect] = useState(
    initialEnableBulkSelect
  );

  const [tasksCount, setTasksCount] = useState(0);

  const isIndeterminateState =
    selectedTasks.length > 0 && selectedTasks.length !== tasksCount;

  const handleSelectedTasks = (
    isChecked: boolean,
    allTasks: Array<SlimTaskInstanceSerializedType>
  ) => {
    setSelectedTasks(isChecked ? allTasks : []);
  };

  const addToSelection = (taskToAdd: SlimTaskInstanceSerializedType) => {
    setSelectedTasks((currentlySelectedTasks) => {
      const taskExists = currentlySelectedTasks.find(
        (selectedTask) => selectedTask.id === taskToAdd.id
      );

      if (taskExists) {
        return currentlySelectedTasks;
      } else {
        return currentlySelectedTasks.concat([taskToAdd]);
      }
    });
  };

  const removeFromSelection = (
    taskToRemove: SlimTaskInstanceSerializedType
  ) => {
    setSelectedTasks((currentlySelectedTasks) =>
      currentlySelectedTasks.filter(
        (selectedTask) => taskToRemove.id !== selectedTask.id
      )
    );
  };

  const isTaskSelected = (task: SlimTaskInstanceSerializedType) => {
    return selectedTasks.some((selectedTask) => selectedTask.id === task.id);
  };

  const contextValue: BulkSelectTasksContextType = {
    selectedTasks,
    setSelectedTasks,
    isIndeterminateState,
    handleSelectedTasks,
    addToSelection,
    removeFromSelection,
    isTaskSelected,
    enableBulkSelect,
    setEnableBulkSelect,
    tasksCount,
    setTasksCount,
  };

  return (
    <BulkSelectContext.Provider value={contextValue}>
      {children}
    </BulkSelectContext.Provider>
  );
}

export function useBulkTasksSelect() {
  const context = useContext(BulkSelectContext);
  if (!context) {
    throw new Error(
      'useBulkTasksSelect must be used within a BulkSelectTasksProvider'
    );
  }
  return context;
}
